import { createContext } from "react";
import type { User } from "firebase/auth";
import type { UserProfile, LanguageIsland } from "@/types/user";

import type { LanguageCode } from "@/utils/translate";

export interface AuthContextType {
  currentUser: User | null;
  userId: string | null;
  userProfile: UserProfile | null;
  loading: boolean;
  nativeLanguage: LanguageCode;
  targetLanguage: LanguageCode;
  setTargetLanguage: (lang: LanguageCode) => void;
  setSessionLanguages: (native: LanguageCode, target: LanguageCode) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (options?: {
    nativeLanguage?: LanguageCode;
    targetLanguage?: LanguageCode;
    newsletterConsentStatus?: "subscribed" | "opted_out" | "undecided";
  }) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    nativeLanguage?: LanguageCode,
    targetLanguage?: LanguageCode,
    newsletterConsentStatus?:
      | "subscribed"
      | "opted_out"
      | "undecided"
  ) => Promise<void>;
  logout: () => Promise<void>;
  updateLanguagePreferences: (
    native: LanguageCode,
    target: LanguageCode
  ) => Promise<void>;
  updateSkipIntro: (skip: boolean) => Promise<void>;
  toggleFavoriteScenario: (scenarioId: string) => Promise<void>;
  markScenarioAsCompleted: (scenarioId: string, venueSvgComponent?: string) => Promise<void>;
  updateNewsletterConsentStatus: (
    status: "subscribed" | "opted_out" | "undecided"
  ) => Promise<void>;
  updateSelectedTtsVoice: (voiceName: string) => Promise<void>;
  addReviewItem: (scenarioId: string, sentencePair: { id: string; native: string; target: string }) => Promise<void>;
  addReviewItemByOrigin: (params: {
    originType: "scenario" | "island" | "phraseSet";
    originId: string;
    sentencePair: { id: string; native: string; target: string };
    phrasePairIndex?: number;
  }) => Promise<void>;
  removeReviewItemsByOrigin: (
    originType: "scenario" | "island" | "phraseSet",
    originId: string
  ) => Promise<void>;
  addIslandToProfile: (island: LanguageIsland) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);
